import { ref, type Ref } from 'vue'

import { usePaginatedList, type ListSort, type UsePaginatedListOptions } from './usePaginatedList'

export type ServerListRequestState<TFilters, TSortField extends string = string> = {
  filters: TFilters
  pageIndex: number
  pageSize: number
  sort: ListSort<TSortField> | null
}

export type ServerListResult<TItem> = {
  items: TItem[]
  totalItems: number
}

export type UseServerListOptions<
  TItem,
  TFilters,
  TRequest,
  TResponse,
  TSortField extends string = string,
> = UsePaginatedListOptions<TSortField> & {
  filters: Ref<TFilters>
  fetch: (request: TRequest) => Promise<TResponse>
  toRequest: (state: ServerListRequestState<TFilters, TSortField>) => TRequest
  fromResponse: (response: TResponse) => ServerListResult<TItem>
}

const toError = (err: unknown) => (err instanceof Error ? err : new Error(String(err)))

export const useServerList = <TItem, TFilters, TRequest, TResponse, TSortField extends string = string>(
  options: UseServerListOptions<TItem, TFilters, TRequest, TResponse, TSortField>,
) => {
  const pagination = usePaginatedList<TSortField>(options)
  const items = ref<TItem[]>([]) as Ref<TItem[]>
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const lastUpdatedAt = ref(0)

  let activeRequestToken = 0

  const search = async () => {
    const token = ++activeRequestToken

    try {
      error.value = null
      isLoading.value = true

      const response = await options.fetch(
        options.toRequest({
          filters: options.filters.value,
          pageIndex: pagination.pageIndex.value,
          pageSize: pagination.pageSize.value,
          sort: pagination.sort.value,
        }),
      )

      if (token !== activeRequestToken) return

      const result = options.fromResponse(response)
      const pageWasClamped = pagination.setTotalItems(result.totalItems)

      if (pageWasClamped) {
        await search()
        return
      }

      items.value = result.items
      lastUpdatedAt.value = Date.now()
    } catch (err) {
      if (token !== activeRequestToken) return
      error.value = toError(err)
    } finally {
      if (token === activeRequestToken) {
        isLoading.value = false
      }
    }
  }

  const setPageIndex = async (pageIndex: number) => {
    if (!pagination.setPageIndex(pageIndex)) return
    await search()
  }

  const setPageSize = async (pageSize: number) => {
    if (!pagination.setPageSize(pageSize)) return
    await search()
  }

  const resetPageIndex = () => pagination.setPageIndex(0)

  const previousPage = async () => {
    if (!pagination.previousPage()) return
    await search()
  }

  const nextPage = async () => {
    if (!pagination.nextPage()) return
    await search()
  }

  const toggleSort = async (field: TSortField) => {
    if (!pagination.toggleSort(field)) return
    await search()
  }

  return {
    ...pagination,
    items,
    isLoading,
    error,
    lastUpdatedAt,
    search,
    setPageIndex,
    setPageSize,
    resetPageIndex,
    previousPage,
    nextPage,
    toggleSort,
  }
}
